import { as, clampNumber, nextTick, wrapNumber } from '@newthink/utilities'
import type { DirectiveCallback } from 'alpinejs'
import { childrenSansPassthrough } from './childrenSansPassthrough'
import { $slider, type SliderCTX } from './SliderCTX'
import { isIMGElement } from './utils/isIMGElement'

export const SliderRoot: DirectiveCallback = (
  el,
  { expression },
  { evaluate, cleanup, Alpine },
) => {
  const cleanups: (() => void)[] = []
  cleanup(() => cleanups.forEach((cleanup) => cleanup()))
  const opts = Object.assign(
    {
      scrollToDepth: 0,
      intoView: false,
      centerVertically: false,
      autoScroll: 0,
      autoInitialize: false,
    },
    expression ? evaluate<object>(expression) : {},
  )

  const ctx = Alpine.reactive<SliderCTX>({
    focusedIndex: 0,
    focusedSize: 0,
    initialized: Boolean(opts.autoInitialize),
    touched: false,
    components: new Map<'gallery' | 'thumbnails', HTMLElement>(),
    init() {
      if (opts.autoScroll) {
        const autoScroller: ReturnType<typeof setInterval> = setInterval(() => {
          if (this.touched) return clearInterval(autoScroller)
          const gallery = this.components.get('gallery')
          if (!gallery) return
          this.scrollTo(
            wrapNumber(
              this.focusedIndex + 1,
              childrenSansPassthrough(gallery).length,
            ),
          )
        }, opts.autoScroll)
        cleanups.push(() => clearInterval(autoScroller))
      }
    },
    onLoad(target: HTMLElement) {
      if (this.initialized || (isIMGElement(target) && !target.complete)) return
      this.focusedIndex = 0
      this.focusedSize = this.itemAtIndex(0)?.clientHeight || 0
      const gallery = this.components.get('gallery')
      if (gallery) gallery.scrollLeft = 0
      this.initialized = true
      this.init()
    },
    handleIntersect(el: HTMLElement) {
      if (!this.initialized) return
      this.focusedIndex = this.indexOfItem(el) ?? 0
      this.focusedSize = el.clientHeight
    },
    itemAtIndex(idx: number): Element | undefined {
      const gallery = this.components.get('gallery')
      if (!gallery) return
      const children = childrenSansPassthrough(gallery)
      let item: Element = children[clampNumber(idx, children.length - 1)]
      if (item === undefined) return
      for (let i = 0; i < opts.scrollToDepth; i++)
        item = item?.firstElementChild ?? item
      if (item?.closest('template')) return this.itemAtIndex(idx + 1)
      return item
    },
    indexOfItem(el: Element) {
      const gallery = this.components.get('gallery')
      if (!gallery) return
      const children = childrenSansPassthrough(gallery)
      for (let i = 0; i < children.length; i++) {
        if (children[i].contains(el)) return i
      }
    },
    async scrollTo(
      target: number | Element,
      offset = 0,
      userInitiated = false,
    ) {
      const gallery = this.components.get('gallery')
      if (!gallery) return
      const el = target instanceof Element ? target : this.itemAtIndex(target)
      if (!el) return
      if (userInitiated) this.touched = true
      const idx =
        typeof target === 'number'
          ? target
          : (this.indexOfItem(el) ?? this.focusedIndex)
      if (!this.initialized) {
        this.initialized = true
        await nextTick()
      }
      this.focusedIndex += Math.sign(idx - this.focusedIndex)
      window.scrollBy({
        top: 0.5,
      }) // hack to break out of broken scrolling 💩
      if (opts.intoView)
        el.scrollIntoView({
          behavior: 'smooth',
          block: opts.centerVertically ? 'center' : 'nearest',
          inline: 'center',
        })
      else
        gallery.scrollLeft =
          target === 0 ? 0 : (as(HTMLElement, el)?.offsetLeft ?? 0) - offset
      const thumbnails = this.components.get('thumbnails')
      if (!thumbnails) return
      const thumb = as(HTMLElement, thumbnails.children[idx])
      if (!thumb) return
      thumbnails.scrollTo({
        top:
          thumb.offsetTop - (thumbnails.clientHeight - thumb.clientHeight) / 2,
        left:
          thumb.offsetLeft - (thumbnails.clientWidth - thumb.clientWidth) / 2,
        behavior: 'smooth',
      })
    },

    get isFirst() {
      return this.focusedIndex === 0
    },
    get isLast() {
      const gallery = this.components.get('gallery')
      if (!gallery) return true
      return this.focusedIndex === childrenSansPassthrough(gallery).length - 1
    },
    get itemCount() {
      const gallery = this.components.get('gallery')
      if (!gallery) return 0
      return childrenSansPassthrough(gallery).length
    },
    next() {
      this.scrollTo(this.focusedIndex + 1)
    },
    prev() {
      this.scrollTo(this.focusedIndex - 1)
    },
  } satisfies SliderCTX)
  Alpine.addScopeToNode(el, { [$slider]: ctx })
  if (opts.autoInitialize) ctx.init()
}
